<template>
  <div :class="wrapperClassName" v-if="visibleIfResult">
    <div class="checkbox">
      <input
        :id="idInput"
        ref="checkboxinput"
        :data-wdw-code="code"
        :data-vv-as="label"
        :name="name"
        :class="inputClassName"
        type="checkbox"
        v-model="value"
        tabindex="0"
        :disabled="disabled"
        :aria-invalid="hasError || hasServerError"
        :aria-describedby="idErrorText"
      />
      <label :for="idInput" :class="labelClassName" v-dompurify-html="labelText"></label>

      <span
        class="help-trigger"
        :title="domainStore.translate('general.form.show-help-tooltip')"
        @click="toggleHelpText"
        v-if="help"
      >
        <font-awesome-icon icon="question-circle" size="lg" />
      </span>
      <Message
        v-show="shouldShowHelp"
        closable
        severity="info"
        role="alert"
        class="mb-2 form-info-message"
        @close="toggleHelpText"
      >
        <span v-dompurify-html="help"></span>
      </Message>
      <div v-show="hasError" :id="idErrorText" aria-atomic="true" class="text-danger form-error-message">
        {{ error }}
      </div>
      <div :id="idErrorText" v-show="hasServerError" class="text-danger form-error-message" aria-atomic="true">
        {{ serverError }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, toRefs } from 'vue';
import { useField } from 'vee-validate';
import useToggler from '@/composables/general/useToggler';
import { domainStore } from '@/store/domain-store';
import Message from 'primevue/message';

export default {
  name: 'form-control-checkbox',

  components: { Message },

  props: {
    name: {
      required: true,
      type: String,
    },
    code: {
      required: false,
      type: String,
      default: '',
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    cachevalue: {
      required: false,
      type: String,
      default: '',
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false,
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    defaultvalue: {
      required: false,
      type: Boolean,
      default: false,
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-bms-attr',
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true,
    },
    isPublic: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    reload: {
      type: Number,
      required: false,
    },
  },

  setup(props, context) {
    const {
      name,
      code,
      label,
      required,
      defaultvalue,
      shouldcache,
      error,
      serverError,
      additionalLabelClasses,
      prefixId,
      isPublic,
    } = toRefs(props);
    const checkboxinput = ref(null);
    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();

    //+---------------------+
    //|    Other functions  |
    //+---------------------+
    function validate(value) {
      const finalvalue = value ? value : false;
      const returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        validated: true,
        name: name.value,
        code: code.value,
        type: 'checkbox',
      };

      if (required.value) {
        returnValue.validated = finalvalue;
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, returnValue.value);
      }

      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }
    const { errorMessage, value } = useField(name, validate, {
      initialValue: defaultvalue.value,
    });
    //+---------------------+
    //|    computed()       |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const wrapperClassName = computed(() => {
      return {
        'has-error': hasError.value || hasServerError.value,
      };
    });
    const inputClassName = computed(() => {
      return {
        'is-invalid': hasError.value || hasServerError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (hasError.value || hasServerError.value ? 'text-danger' : '');
    });
    const labelText = computed(() => {
      return (
        label.value +
        (required.value ? '*' : '') +
        (isPublic.value
          ? ''
          : ' - <span class="text-muted">' + domainStore.translate('general.form.private-data') + '</span>')
      );
    });
    const idInput = computed(() => {
      return prefixId.value + '-checkbox-' + name.value;
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-checkbox-' + name.value + '-error';
    });

    //+---------------------+
    //|    watchers         |
    //+---------------------+
    watch(
      () => props.reload,
      (nv, ov) => {
        value.value = defaultvalue.value;
      }
    );

    //+---------------------+
    //|    Lifecycle hooks  |
    //+---------------------+
    onMounted(() => {
      const chbinputref = checkboxinput.value;
      chbinputref.oninvalid = chbinputref.setCustomValidity(error.value);
    });

    return {
      // Computed
      domainStore,
      hasError,
      hasServerError,
      wrapperClassName,
      labelClassName,
      inputClassName,
      idInput,
      idErrorText,
      labelText,
      // References to DOM
      checkboxinput,
      // Other methods
      toggleHelpText,
      // Data
      shouldShowHelp,
      errorMessage,
      value,
    };
  },
};
</script>

<style lang="scss" scoped>
input {
  cursor: pointer;
}
label {
  display: inline !important;
  cursor: pointer;
}
.checkbox {
  margin-bottom: 10px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
</style>
